import { Map as MapIcon, ArrowRight } from 'lucide-react'
import { Link } from 'react-router-dom'
import type { OverlapGroup } from '../../types/whitespace'

/**
 * "Related in your set" — contract row S3's second part (design #5630, Figure 4).
 *
 * Tells an inventor, on the invention page, that another invention of THEIR OWN
 * shares ground with this one, and sends them to the atlas to see it.
 *
 * THE RULE THAT SHAPES THIS COMPONENT: the Invention scope must never trigger a
 * portfolio computation. Opening one invention is the cheapest thing a user
 * does; making it start pair mathematics across their whole set would be a
 * surprise cost on a page load. So this renders ONLY from an overlap result
 * that has ALREADY been computed and cached, and it renders NOTHING — no empty
 * box, no skeleton, no "no overlaps yet" — when there is no cached result, or
 * when the cached result does not name this project.
 *
 * That silence is the feature, not an oversight. It is also why the fetcher
 * behind it must be the cache-only read: a fetcher that enqueues on a miss
 * would defeat the whole rule from one layer down, however this component
 * behaves.
 */
export interface RelatedInYourSetProps {
  /** The invention being viewed. */
  projectId: string
  /** Groups from an ALREADY-CACHED own-scope overlap result. Undefined = no cached result. */
  groups?: OverlapGroup[]
  /** project_id -> display name, from the projects the page already holds. */
  projectNames?: Record<string, string>
}

export default function RelatedInYourSet({
  projectId,
  groups,
  projectNames,
}: RelatedInYourSetProps) {
  // Every other invention of theirs that shares a cached overlap group with
  // this one. A group that names only this project is not a relationship.
  // leadScore is the score of the group that first names the lead invention —
  // the same score the atlas (S4) and overlap view (S4b) show for this pair.
  const related: string[] = []
  let leadScore: number | undefined
  for (const group of groups ?? []) {
    if (!group.member_project_ids?.includes(projectId)) continue
    for (const id of group.member_project_ids) {
      if (id !== projectId && !related.includes(id)) {
        related.push(id)
        if (leadScore === undefined) leadScore = group.score
      }
    }
  }

  if (related.length === 0) return null

  const name = (id: string) => projectNames?.[id] ?? id
  const lead = name(related[0])
  const others = related.length - 1

  return (
    <div
      data-part="RelatedInYourSet"
      className="mb-4 flex items-center gap-2 rounded-lg border border-dashed border-iflow-300 bg-white px-3.5 py-2"
    >
      <MapIcon size={14} className="shrink-0 text-iflow-600" />
      <p className="text-[12px] text-gray-700">
        <span>Related in your set:</span>{' '}
        <b className="text-iflow-900">“{lead}”</b> shares ground with this one
        {leadScore !== undefined && ` (${leadScore})`}
        {others > 0 && ` (and ${others} other${others === 1 ? '' : 's'})`}
        {' — '}
        <Link
          to="/projects?view=overlap"
          title="Opens My inventions with both footprints highlighted"
          className="inline-flex items-center gap-0.5 font-bold text-iflow-600 hover:text-iflow-700"
        >
          see them on the atlas
          <ArrowRight size={12} />
        </Link>
      </p>
    </div>
  )
}
